import { useTranslations } from 'next-intl'
import { memo } from 'react'
import { useRestaurantDetailContext } from '@/features/restaurants/restaurant-detail/restaurant-detail-context'
import {
    buildRestaurantMapsUrl,
    formatRestaurantAddress,
} from '@/features/restaurants/utils/restaurant-address'
import { SvgOutlinedPlace } from '@/icons/svg-outlined-place'
import { cn } from '@/utils/styles/cn'
import { COLORS } from '@/utils/styles/colors'

interface IRestaurantDetailLocationProps {
    styleType?: 'compact' | 'full'
}

export const RestaurantDetailLocation = memo(
    ({ styleType = 'compact' }: IRestaurantDetailLocationProps) => {
        const { restaurant } = useRestaurantDetailContext()

        const t = useTranslations()

        if (!restaurant || (!restaurant.address && !restaurant.city && !restaurant.zipCode))
            return null

        const fullAddress = formatRestaurantAddress(restaurant)
        const mapsUrl = buildRestaurantMapsUrl(restaurant)

        const content =
            styleType === 'full' ? (
                <div className="flex flex-row items-center justify-start gap-x-2 md:gap-x-1.5">
                    <SvgOutlinedPlace
                        color={COLORS.fg.primary}
                        size={19}
                        style={{ position: 'relative', bottom: '0.5px' }}
                    />

                    <p
                        className="font-medium md:font-normal text-[15px] md:text-[16px] underline underline-offset-4
                            decoration-black/20 group-hover:decoration-black transition-colors"
                    >
                        {fullAddress}
                    </p>
                </div>
            ) : (
                <div className="flex flex-row items-center justify-start w-full gap-x-2 md:gap-x-1.5 overflow-hidden">
                    <div className="md:block hidden">
                        <SvgOutlinedPlace
                            color="black"
                            size={19}
                            style={{ position: 'relative', bottom: '0.5px' }}
                        />
                    </div>

                    {restaurant.zipCode && (
                        <p className="font-semibold hidden md:block text-[15px] md:text-[16px] whitespace-nowrap">
                            {restaurant.zipCode} {restaurant.city?.name}
                        </p>
                    )}

                    {restaurant.address && (
                        <p
                            className="font-medium md:font-normal text-[15px] md:text-[16px] text-ellipsis overflow-hidden
                                whitespace-nowrap relative group-hover:underline underline-offset-4"
                        >
                            {restaurant.address}
                        </p>
                    )}
                </div>
            )

        const className = cn(
            'flex flex-row items-center justify-between px-1',
            styleType === 'full' ? 'w-fit max-w-full' : 'w-full md:w-fit'
        )

        if (!mapsUrl) return <div className={className}>{content}</div>

        return (
            <a
                href={mapsUrl}
                target="_blank"
                rel="noreferrer"
                aria-label={t('restaurants.detail.location.openInMaps', { address: fullAddress })}
                className={cn(className, 'group')}
            >
                {content}
            </a>
        )
    }
)

RestaurantDetailLocation.displayName = 'RestaurantDetailLocation'
